import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileDown, AppWindow, Loader2, Smartphone } from "lucide-react";
import { API, http, errMsg, downloadFile } from "../lib/api";
import { toast } from "sonner";

const FileButton = ({ token, kind, icon: Icon, label, hint }) => {
  const [busy, setBusy] = useState(false);
  return (
    <a data-testid={`delivery-download-${kind}`} href={`${API}/delivery/${token}/${kind}`}
      onClick={async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try { await downloadFile(`/delivery/${token}/${kind}`, `PB-Travel-Angel.${kind === "pdf" ? "pdf" : "html"}`); }
        catch (err) { toast.error(errMsg(err)); }
        finally { setBusy(false); }
      }}
      className="card-lift flex items-center gap-4 rounded-xl border border-stone-300 bg-white p-5">
      <div className="h-12 w-12 rounded-sm bg-[#C9A847]/15 grid place-items-center text-[#9A7B2C]">{busy ? <Loader2 className="animate-spin" size={20} /> : <Icon size={20} />}</div>
      <div><div className="font-display text-xl text-[#2B2A26]">{label}</div><div className="text-xs text-stone-500">{hint}</div></div>
    </a>
  );
};

export default function Delivery() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get(`/delivery/${token}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  if (err) return <div className="grain min-h-screen grid place-items-center p-8"><div data-testid="delivery-error" className="text-center"><div className="font-display text-3xl text-[#2B2A26]">Link unavailable</div><p className="text-stone-500 mt-2">{err}</p></div></div>;
  if (!d) return <div className="min-h-screen grid place-items-center eyebrow">Loading your kit…</div>;
  return (
    <div className="grain min-h-screen grid lg:grid-cols-2">
      <div className="relative min-h-[40vh] lg:min-h-screen bg-[#1A1710]">
        {d.has_cover && <img data-testid="delivery-cover" src={`${API}/delivery/${token}/cover`} alt={d.destination} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C0B09] via-[#0C0B09]/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10"><div className="eyebrow">{[d.edition, d.tier].filter(Boolean).join(" · ")}</div><div className="font-display text-4xl lg:text-5xl text-white mt-2">{d.destination}</div>{d.tagline && <p className="font-display italic text-stone-200 mt-3">{d.tagline}</p>}</div>
      </div>
      <div className="flex items-center px-8 sm:px-16 py-14 relative z-[1]">
        <div className="w-full max-w-md rise">
          <div className="eyebrow">PB Travel Angel · Order {d.order_id}</div>
          <h1 data-testid="delivery-title" className="font-display text-4xl text-[#2B2A26] mt-3">{d.customer_name ? `${d.customer_name}, your` : "Your"} kit is ready.</h1>
          <p className="text-stone-500 mt-3">{d.title} — {d.subtitle}</p>
          <div className="space-y-4 mt-10">
            <FileButton token={token} kind="pdf" icon={FileDown} label="Download PDF kit" hint="Print-ready A4 guide" />
            {!d.pdf_only && (
              <a data-testid="delivery-open-phone-app" href={`${API}/delivery/${token}/live`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-4 rounded-xl border border-[#C9A847] bg-[#C9A847]/10 p-5 hover:bg-[#C9A847]/20 transition-colors">
                <Smartphone size={22} className="text-[#9A7B2C]" />
                <div><div className="font-medium">Open the phone app</div><div className="text-xs text-stone-500">Best on your phone — then tap “Add to Home Screen” to keep it offline</div></div>
              </a>
            )}
            {!d.pdf_only && <FileButton token={token} kind="app" icon={AppWindow} label="Download interactive app" hint="Open the .html file in any browser — works offline" />}
          </div>
          <p className="text-xs text-stone-500 mt-8">Link valid until {new Date(d.expires_at).toLocaleDateString()}. Book. Pack. Go.</p>
        </div>
      </div>
    </div>
  );
}
